import {
  type ConnectorStatus,
  connectorStatusColors,
} from "../../lib/connectorStatus";
import { StatusPill } from "../StatusPill";
import { Text } from "../Text";

type Props = {
  status: ConnectorStatus;
  label: string;
  detail: string | null;
  httpStatusCode?: string;
  running: boolean;
  untestable: boolean;
  onTest?: () => void;
  detailsUrl?: string;
};

const descriptions: Record<ConnectorStatus, string> = {
  active: "Ready to use.",
  untested: "Test this connection",
  error: "Connection test failed",
  draft: "Finish connector setup",
  disabled: "Calls through this connector are disabled.",
};

const compactWidths: Record<ConnectorStatus, number> = {
  active: 58,
  untested: 70,
  error: 52,
  draft: 52,
  disabled: 70,
};

export function ConnectorStatusBadge({
  status,
  label,
  detail,
  httpStatusCode,
  running,
  untestable,
  onTest,
  detailsUrl,
}: Props) {
  return (
    <StatusPill
      color={connectorStatusColors[status]}
      label={label}
      detail={
        <Text variant="text-small-md" color="var(--tooltip-fg)">
          {httpStatusCode && !running && (
            <Text variant="text-small-md" color="var(--orange-600)">
              {httpStatusCode}:{" "}
            </Text>
          )}
          {running ? "Testing connection…" : (detail ?? descriptions[status])}
        </Text>
      }
      running={running}
      action={
        onTest
          ? { label: "Test connection", onClick: onTest, disabled: untestable }
          : undefined
      }
      detailsUrl={detailsUrl}
      triggerLabel={
        detailsUrl
          ? `${label}: view connection details`
          : `${label}${running ? ", testing connection" : ""}`
      }
      compactAt={compactWidths[status]}
      compactIcon={
        status === "active"
          ? "check"
          : status === "error"
            ? "x"
            : "circle-dashed"
      }
    />
  );
}
